import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList } from 'recharts';

// Skeleton loader component for the chart
const ChartSkeletonLoader = () => {
  const skeletonRows = 8;
  const rowHeight = 50;
  const baseHeight = 100;
  const skeletonHeight = skeletonRows * rowHeight + baseHeight;
  
  // Fixed widths for skeleton bars to avoid random values
  const barWidths = [45, 60, 35, 55, 40, 50, 38, 48];
  
  return (
    <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, skeletonHeight) }}>
      <div style={{ 
        width: '100%', 
        height: '100%', 
        padding: '20px 42px 5px 0px',
        position: 'relative'
      }}>
        {/* Y-axis skeleton labels */}
        <div style={{ 
          position: 'absolute', 
          left: '0', 
          top: '20px', 
          bottom: '5px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          paddingRight: '10px',
          width: '100px'
        }}>
          {Array.from({ length: skeletonRows }).map((_, index) => (
            <div
              key={index}
              style={{
                width: '80px',
                height: '13px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ))}
        </div>

        {/* Chart area with bars */}
        <div style={{ 
          marginLeft: '100px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          paddingTop: '10px',
          paddingBottom: '30px',
          position: 'relative'
        }}>
          {/* Vertical grid lines skeleton */}
          <div style={{ 
            position: 'absolute',
            top: '10px',
            left: '0',
            right: '42px',
            bottom: '30px',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '1px',
                  height: '100%',
                  background: 'repeating-linear-gradient(to bottom, #D6D9DB 0px, #D6D9DB 3px, transparent 3px, transparent 6px)',
                  opacity: 0.3
                }}
              />
            ))}
          </div>

          {/* Two bars per row skeleton - positioned side by side horizontally */}
          {Array.from({ length: skeletonRows }).map((_, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '50px',
                position: 'relative',
                gap: '4px'
              }}
            >
              {/* Current Year bar skeleton - starts from left */}
              <div
                style={{
                  width: `${barWidths[index] || 45}%`,
                  height: '13px',
                  backgroundColor: '#87ABE2',
                  opacity: 0.3,
                  borderRadius: '2px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  minWidth: '60px'
                }}
              />
              {/* Last Year bar skeleton - positioned right next to current year */}
              <div
                style={{
                  width: `${(barWidths[index] || 45) - 10}%`,
                  height: '13px',
                  backgroundColor: '#C7B6F6',
                  opacity: 0.3,
                  borderRadius: '2px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: '0.2s',
                  minWidth: '50px'
                }}
              />
            </div>
          ))}
        </div>

        {/* X-axis skeleton ticks */}
        <div style={{ 
          position: 'absolute', 
          bottom: '5px', 
          left: '100px', 
          right: '42px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end'
        }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              style={{
                width: '40px',
                height: '12px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ))}
        </div>

        {/* Legend skeleton - positioned at top right */}
        <div style={{ 
          position: 'absolute',
          top: '0',
          right: '42px',
          display: 'flex',
          gap: '20px',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '13px',
              height: '13px',
              backgroundColor: '#87ABE2',
              borderRadius: '2px',
              opacity: 0.3,
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
            <div style={{
              width: '100px',
              height: '12px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '13px',
              height: '13px',
              backgroundColor: '#C7B6F6',
              borderRadius: '2px',
              opacity: 0.3,
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
            <div style={{
              width: '100px',
              height: '12px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
          </div>
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

const useProcessedData = (trackReport, trackReportLoading, isError, reportingOverview) => {
  return useMemo(() => {
    try {
      if (trackReportLoading || isError) return [];

      // Validate input array
      if (!Array.isArray(trackReport)) {
        return [];
      }

      // Validate reportingOverview
      if (!reportingOverview) {
        return [];
      }

      const currentYear = reportingOverview?.year || new Date().getFullYear();
      const previousYear = currentYear - 1;

      const calculateTotalDuration = (projects, year) => {
        try {
          if (!Array.isArray(projects)) {
            return [];
          }

          const clientsData = projects.reduce((acc, project) => {
            try {
              if (!project || !project.startTime) return acc;

              const projectYear = new Date(project.startTime).getFullYear();
              if (isNaN(projectYear)) return acc;

              const clientName = project?.clientId?.client_name;
              const clientId = project?.clientId?._id;

              if (!clientName) return acc;

              if (!acc[clientName]) {
                acc[clientName] = { name: clientName, duration: 0 };
              }

              const projectId = project?.project?._id;
              const preferredCurrency = project?.clientId?.preferred_currency || 'USD';
              const isProjectDeleted = project?.project?.is_delete === true;
              const projectDuration = project?.duration || 0;

              const isProjectIncluded = Array.isArray(reportingOverview?.project) && 
                projectId && 
                reportingOverview.project.includes(projectId);
              
              const isCurrencyIncluded = Array.isArray(reportingOverview?.currency) && 
                reportingOverview.currency.includes(preferredCurrency);

              if (
                projectYear === year &&
                !isProjectDeleted &&
                isProjectIncluded &&
                isCurrencyIncluded
              ) {
                const duration = parseFloat(projectDuration) || 0;
                if (!isNaN(duration)) {
                  acc[clientName].duration += duration;
                }
              }

              return acc;
            } catch (error) {
              console.error('Error processing project in calculateTotalDuration:', error);
              return acc;
            }
          }, {});

          return Object.values(clientsData).map(client => {
            try {
              const duration = parseFloat(client.duration) || 0;
              return {
                name: client.name || 'Unknown',
                duration: isNaN(duration) ? 0 : duration.toFixed(2)
              };
            } catch (error) {
              console.error('Error mapping client data:', error);
              return {
                name: client.name || 'Unknown',
                duration: '0.00'
              };
            }
          });
        } catch (error) {
          console.error('Error in calculateTotalDuration:', error);
          return [];
        }
      };

      const currentYearData = calculateTotalDuration(trackReport, currentYear);
      const previousYearData = calculateTotalDuration(trackReport, previousYear);

      return currentYearData.map(data => {
        try {
          const currentYearValue = parseFloat(data.duration) || 0;
          const prevData = previousYearData.find(prevData => prevData && prevData.name === data.name);
          const lastYearValue = parseFloat(prevData?.duration || 0) || 0;

          return {
            name: data.name || 'Unknown',
            currentYear: isNaN(currentYearValue) ? 0 : currentYearValue,
            lastYear: isNaN(lastYearValue) ? 0 : lastYearValue
          };
        } catch (error) {
          console.error('Error mapping processed data:', error);
          return {
            name: data.name || 'Unknown',
            currentYear: 0,
            lastYear: 0
          };
        }
      });
    } catch (error) {
      console.error('Error in useProcessedData:', error);
      return [];
    }
  }, [trackReport, trackReportLoading, isError, reportingOverview]);
};

const ReportOverviewHoursPerClientGraph = ({ trackReport, trackReportLoading, trackReportError }) => {
  const reportingOverview = useSelector((state) => state.reportingOverview);
  const processedData = useProcessedData(trackReport, trackReportLoading, trackReportError, reportingOverview);

  const currentYear = reportingOverview?.year || new Date().getFullYear();
  const previousYear = currentYear - 1;
  const isCurrentYear = currentYear === new Date().getFullYear();

  // Show skeleton loader while loading
  if (trackReportLoading) {
    return <ChartSkeletonLoader />;
  }

  // Show error message if there's an error
  if (trackReportError) {
    return (
      <div className="outter-load-table" style={{ width: '100%', height: '542px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Error loading data. Please try again.</p>
        </div>
      </div>
    );
  }

  const changeDurationFormat = (sec) => {
    try {
      if (sec === null || sec === undefined || isNaN(sec)) {
        return '00:00:00';
      }

      const seconds = Math.floor(Math.abs(parseFloat(sec)));
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } catch (error) {
      console.error('Error in changeDurationFormat:', error);
      return '00:00:00';
    }
  };

  const calculateXTicks = () => {
    try {
      if (!Array.isArray(processedData) || processedData.length === 0) {
        return [0];
      }

      const validData = processedData.filter(item => 
        item && 
        typeof item.currentYear === 'number' && 
        typeof item.lastYear === 'number' &&
        !isNaN(item.currentYear) &&
        !isNaN(item.lastYear)
      );

      if (validData.length === 0) {
        return [0];
      }

      const maxDataValue = Math.max(...validData.map(item => Math.max(item.currentYear || 0, item.lastYear || 0)));
      
      if (maxDataValue === 0 || isNaN(maxDataValue)) {
        return [0];
      }

      const tickInterval = Math.ceil(maxDataValue / 10);
      if (tickInterval === 0 || isNaN(tickInterval)) {
        return [0];
      }

      const numOfTicks = Math.ceil(maxDataValue / tickInterval);
      const ticks = [];

      for (let i = 0; i <= numOfTicks + 4; i++) {
        const tickValue = i * tickInterval;
        if (!isNaN(tickValue)) {
          ticks.push(tickValue);
        }
      }

      return ticks.length > 0 ? ticks : [0];
    } catch (error) {
      console.error('Error in calculateXTicks:', error);
      return [0];
    }
  };

  const xAxisTicks = calculateXTicks();

  const calculateChartHeight = (dataLength) => {
    try {
      const length = parseInt(dataLength) || 0;
      if (isNaN(length) || length < 0) {
        return 542;
      }

      const rowHeight = 50;
      const baseHeight = 100;
      const calculatedHeight = length * rowHeight + baseHeight;
      
      return isNaN(calculatedHeight) ? 542 : Math.max(542, calculatedHeight);
    } catch (error) {
      console.error('Error in calculateChartHeight:', error);
      return 542;
    }
  };

  // Ensure processedData is an array
  const safeProcessedData = Array.isArray(processedData) ? processedData : [];

  try {
    return (
      <div className='graph-project-per-hour' style={{ width: '100%', height: calculateChartHeight(safeProcessedData.length) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={safeProcessedData}
            barSize={13}
            margin={{
              top: 20,
              right: 42,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
            <XAxis
              type="number"
              domain={[0, 'dataMax + 10']}
              tickFormatter={(tick) => changeDurationFormat(tick)}
              ticks={xAxisTicks}
            />
            <YAxis
              type="category"
              dataKey="name"
              interval={0}
              minTickGap={-10}
              width={100}
              tickMargin={10}
            />
            <Tooltip formatter={(value) => changeDurationFormat(value)} />
            <Legend />
            <Bar 
              dataKey="currentYear" 
              name={isCurrentYear ? `Current Year (${currentYear})` : `Selected Year (${currentYear})`} 
              fill="#87ABE2"
            >
              <LabelList 
                dataKey="currentYear" 
                position="right" 
                formatter={(value) => value && value !== 0 ? changeDurationFormat(value) : null} 
              />
            </Bar>
            <Bar 
              dataKey="lastYear" 
              name={isCurrentYear ? `Last Year (${previousYear})` : `Previous Year (${previousYear})`} 
              fill="#C7B6F6"
            >
              <LabelList 
                dataKey="lastYear" 
                position="right" 
                formatter={(value) => value && value !== 0 ? changeDurationFormat(value) : null} 
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  } catch (error) {
    console.error('Error rendering ReportOverviewHoursPerClientGraph:', error);
    return (
      <div className="outter-load-table" style={{ width: '100%', height: '542px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Error rendering chart. Please try again.</p>
        </div>
      </div>
    );
  }
}

export default ReportOverviewHoursPerClientGraph;